import { motion } from "framer-motion";
import {
  Users,
  Gauge,
  Hash,
  CheckCircle,
  XCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";

const iconMap = {
  users: Users,
  gauge: Gauge,
  hash: Hash,
  check: CheckCircle,
  x: XCircle,
};

const Stats = ({ summary = [], categoryStats = [], loading }) => {
  const { theme, colors } = useTheme();

  if (loading) {
    return (
      <div className="p-10 text-center" style={{ color: colors.text }}>
        <div className="flex items-center justify-center h-full pt-5">
          <Spin size="large" />
        </div>
      </div>
    );
  }
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };
  const sortedCategoryStats = useMemo(() => {
    let data = [...categoryStats];

    if (sortConfig.key !== null) {
      data.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        return sortConfig.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }

    return data;
  }, [categoryStats, sortConfig]);
  return (
    <div className="space-y-6 mb-8">
      {/* TOP SUMMARY CARDS */}
      <motion.div
        className="grid grid-cols-4 gap-4 max-[992px]:grid-cols-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {summary.map((stat, i) => {
          const Icon = iconMap[stat.icon];
          return (
            <motion.div
              key={i}
              className="backdrop-blur-sm rounded-xl border p-6 group relative transition-all"
              style={{
                background: colors.cardBg,
                borderColor: colors.border,
              }}
              whileHover={{
                y: -4,
                boxShadow: `0 0 20px ${colors.primary}30`,
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div
                  className="text-sm font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  {stat.label}
                </div>
                {Icon && (
                  <div
                    className="p-2 rounded-md border"
                    style={{
                      background:
                        stat.color === "text-green-400"
                          ? "rgba(34,197,94,0.1)"
                          : stat.color === "text-red-400"
                            ? "rgba(239,68,68,0.1)"
                            : `${colors.primary}15`,
                      borderColor:
                        stat.color === "text-green-400"
                          ? "rgba(34,197,94,0.3)"
                          : stat.color === "text-red-400"
                            ? "rgba(239,68,68,0.3)"
                            : `${colors.primary}30`,
                    }}
                  >
                    <Icon
                      className="w-5 h-5"
                      style={{
                        color:
                          stat.color === "text-green-400"
                            ? "#22c55e"
                            : stat.color === "text-red-400"
                              ? "#ef4444"
                              : colors.primary,
                      }}
                    />
                  </div>
                )}
              </div>

              <div
                className="text-4xl font-bold bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    !stat.color &&
                    `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color:
                    stat.color === "text-green-400"
                      ? "#22c55e"
                      : stat.color === "text-red-400"
                        ? "#ef4444"
                        : undefined,
                }}
              >
                {stat.value}
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* CATEGORY TABLE */}
      <motion.div
        className="backdrop-blur-sm rounded-xl border overflow-hidden"
        style={{
          background: colors.cardBg,
          borderColor: colors.border,
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead
              style={{
                backgroundColor:
                  theme === "white" ? "#f8f9fa" : `${colors.primary}08`,
                borderTop: `1px solid ${colors.border}`,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <tr
                style={{
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                {/* <th
                className="text-left py-4 px-6 font-semibold text-sm"
                style={{ color: colors.textSecondary }}
              >
                Category
              </th> */}
                <th
                  onClick={() => handleSort(0)}
                  className="text-left py-4 px-6 font-semibold text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center gap-2">
                    Category
                    {sortConfig.key === 0 ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-4 h-4" />
                      ) : (
                        <ArrowDown className="w-4 h-4" />
                      )
                    ) : (
                      <ArrowUpDown className="w-4 h-4" />
                    )}
                  </span>
                </th>
                {/* <th
                className="text-center py-4 px-6 font-semibold text-sm"
                style={{ color: colors.textSecondary }}
              >
                Operational
              </th> */}
                <th
                  onClick={() => handleSort(1)}
                  className="text-center py-4 px-6 font-semibold text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center justify-center gap-2">
                    Operational
                    {sortConfig.key === 1 ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-4 h-4" />
                      ) : (
                        <ArrowDown className="w-4 h-4" />
                      )
                    ) : (
                      <ArrowUpDown className="w-4 h-4" />
                    )}
                  </span>
                </th>
                {/* <th
                className="text-center py-4 px-6 font-semibold text-sm"
                style={{ color: colors.textSecondary }}
              >
                Non-Operational
              </th> */}
                <th
                  onClick={() => handleSort(2)}
                  className="text-center py-4 px-6 font-semibold text-sm cursor-pointer"
                  style={{ color: colors.textSecondary }}
                >
                  <span className="flex items-center justify-center gap-2">
                    Non-Operational
                    {sortConfig.key === 2 ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-4 h-4" />
                      ) : (
                        <ArrowDown className="w-4 h-4" />
                      )
                    ) : (
                      <ArrowUpDown className="w-4 h-4" />
                    )}
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              {/* {categoryStats.map(([cat, op, nonop], i) => ( */}
              {sortedCategoryStats.map(([cat, op, nonop], i) => (
                <tr
                  key={i}
                  className="transition-colors"
                  style={{
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = `${colors.primary}10`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <td className="py-4 px-6" style={{ color: colors.text }}>
                    {cat}
                  </td>
                  <td
                    className="py-4 px-6 text-center"
                    style={{ color: colors.text }}
                  >
                    {op}
                  </td>
                  <td
                    className="py-4 px-6 text-center"
                    style={{ color: colors.text }}
                  >
                    {nonop}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default Stats;
